// Scrolls one container of the phone preview (the Home scroll view or a
// carousel rail) so a selected section or card is on screen, without touching
// the Studio page scroll the way Element.scrollIntoView would.

// Breathing room above a revealed section so its hover label is not clipped.
const REVEAL_MARGIN = 12

/**
 * The scroll offset that brings a child into view, or null when it already is.
 * `align: 'nearest'` moves the least distance; `align: 'start'` pins the child's
 * leading edge (minus the container's scroll padding) the way scroll snapping does.
 * Children taller than the container align to their start, and a child that
 * already fills the container counts as visible.
 */
export function revealOffset({ start, size, viewport, current, pad = 0, align = 'nearest' }) {
  const leading = Math.max(0, start - pad)
  const trailing = start + size
  const fullyVisible = leading >= current && trailing <= current + viewport
  const fillsViewport = size >= viewport && start <= current && trailing >= current + viewport
  if (fullyVisible || fillsViewport) return null
  if (align === 'start' || size >= viewport || leading < current) return leading
  return trailing - viewport
}

function scrollPadding(scroller, axis) {
  const style = window.getComputedStyle(scroller)
  const value = Number.parseFloat(axis === 'y' ? style.scrollPaddingTop : style.scrollPaddingLeft)
  return Number.isFinite(value) ? value : 0
}

/**
 * Scrolls `scroller` along `axis` until `element` is visible. Geometry comes from
 * the viewport, where the phone is drawn scaled, so distances are divided by the
 * effective scale before being applied as scroll offsets.
 */
export function revealWithin(scroller, element, axis, align = 'nearest') {
  if (!scroller || !element) return
  const rect = element.getBoundingClientRect()
  const box = scroller.getBoundingClientRect()
  const scale = element.offsetWidth > 0 && rect.width > 0 ? rect.width / element.offsetWidth : 1
  const vertical = axis === 'y'
  const offset = revealOffset({
    start: (vertical ? rect.top - box.top : rect.left - box.left) / scale + (vertical ? scroller.scrollTop : scroller.scrollLeft),
    size: (vertical ? rect.height : rect.width) / scale,
    viewport: vertical ? scroller.clientHeight : scroller.clientWidth,
    current: vertical ? scroller.scrollTop : scroller.scrollLeft,
    pad: scrollPadding(scroller, axis) || (vertical && align === 'nearest' ? REVEAL_MARGIN : 0),
    align,
  })
  if (offset === null) return
  const options = vertical ? { top: offset, behavior: 'smooth' } : { left: offset, behavior: 'smooth' }
  if (typeof scroller.scrollTo === 'function') scroller.scrollTo(options)
  else if (vertical) scroller.scrollTop = offset
  else scroller.scrollLeft = offset
}

/**
 * Brings a preview element on screen: first inside the nearest horizontal rail
 * (carousel cards), then inside the Home scroll view. Containers opt in with
 * `data-scroller="x"` / `data-scroller="y"`. A card reveals its whole section
 * (title included) whenever the section fits the phone; only sections taller
 * than the phone reveal the card alone.
 */
export function revealPreviewElement(element, { railAlign = 'start' } = {}) {
  if (!element) return
  revealWithin(element.closest('[data-scroller="x"]'), element, 'x', railAlign)
  const phone = element.closest('[data-scroller="y"]')
  const section = element.closest('[data-container]')
  const sectionFits = phone && section && section.getBoundingClientRect().height <= phone.getBoundingClientRect().height
  revealWithin(phone, sectionFits ? section : element, 'y', 'nearest')
}
